"use client";

import { useState, useMemo, useCallback } from "react";
import styles from "./ReferenceTable.module.css";

interface ColumnDef {
  key: string;
  label: string;
  width?: string;
  sortable?: boolean;
  type?: "color";
}

interface ReferenceTableProps {
  data: Record<string, unknown>[];
  columns: ColumnDef[];
  searchPlaceholder?: string;
  pageSize?: number;
}

type SortDir = "asc" | "desc";

export default function ReferenceTable({
  data,
  columns,
  searchPlaceholder = "Search...",
  pageSize = 50,
}: ReferenceTableProps) {
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [page, setPage] = useState(0);
  const [copiedCell, setCopiedCell] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (!search) return data;
    const q = search.toLowerCase();
    return data.filter((row) =>
      columns.some((col) => {
        const val = row[col.key];
        return typeof val === "string" && val.toLowerCase().includes(q);
      })
    );
  }, [data, columns, search]);

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    return [...filtered].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }
      const aStr = String(aVal);
      const bStr = String(bVal);
      return sortDir === "asc"
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const start = safePage * pageSize;
  const pageData = sorted.slice(start, start + pageSize);

  const handleSort = useCallback(
    (key: string) => {
      if (sortKey === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortKey(key);
        setSortDir("asc");
      }
    },
    [sortKey]
  );

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(0);
  }, []);

  const handleCopy = useCallback(async (text: string, cellId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCell(cellId);
      setTimeout(() => setCopiedCell(null), 1500);
    } catch {
      // clipboard not available
    }
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <input
          type="text"
          value={search}
          onChange={handleSearch}
          placeholder={searchPlaceholder}
          className={styles.searchInput}
          aria-label="Search table"
        />
        <span className={styles.counter}>
          {sorted.length === data.length
            ? `${data.length} entries`
            : `${sorted.length} of ${data.length} entries`}
        </span>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={col.width ? { width: col.width } : undefined}
                  className={
                    col.sortable !== false ? styles.sortable : undefined
                  }
                  onClick={
                    col.sortable !== false
                      ? () => handleSort(col.key)
                      : undefined
                  }
                  aria-sort={
                    sortKey === col.key
                      ? sortDir === "asc"
                        ? "ascending"
                        : "descending"
                      : undefined
                  }
                >
                  {col.label}
                  {sortKey === col.key && (
                    <span className={styles.sortArrow}>
                      {sortDir === "asc" ? " ▲" : " ▼"}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className={styles.empty}>
                  No results found.
                </td>
              </tr>
            ) : (
              pageData.map((row, i) => (
                <tr key={start + i}>
                  {columns.map((col) => {
                    const val = row[col.key];
                    const cellId = `${start + i}-${col.key}`;
                    const isHashOrId =
                      col.key === "hash" ||
                      col.key === "id" ||
                      col.key === "model";
                    return (
                      <td key={col.key}>
                        {col.type === "color" && val != null ? (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
                            <span style={{ display: "inline-block", width: "1rem", height: "1rem", borderRadius: "0.25rem", backgroundColor: String(val), border: "1px solid rgba(128,128,128,0.3)" }} />
                            <code>{String(val)}</code>
                          </span>
                        ) : isHashOrId && val != null ? (
                          <button
                            type="button"
                            className={styles.copyCell}
                            onClick={() => handleCopy(String(val), cellId)}
                            title="Copy to clipboard"
                          >
                            <code>{String(val)}</code>
                            <span className={styles.copyIcon}>
                              {copiedCell === cellId ? "✓" : "⧉"}
                            </span>
                          </button>
                        ) : (
                          String(val ?? "")
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            type="button"
            disabled={safePage === 0}
            onClick={() => setPage(0)}
            className={styles.pageBtn}
          >
            ««
          </button>
          <button
            type="button"
            disabled={safePage === 0}
            onClick={() => setPage((p) => p - 1)}
            className={styles.pageBtn}
          >
            «
          </button>
          <span className={styles.pageInfo}>
            Page {safePage + 1} of {totalPages}
          </span>
          <button
            type="button"
            disabled={safePage >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
            className={styles.pageBtn}
          >
            »
          </button>
          <button
            type="button"
            disabled={safePage >= totalPages - 1}
            onClick={() => setPage(totalPages - 1)}
            className={styles.pageBtn}
          >
            »»
          </button>
        </div>
      )}
    </div>
  );
}

export type { ColumnDef, ReferenceTableProps };
